// src/pages/HomePage.js
// Updated: 11/17/2025 01:06 PM

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { doc, onSnapshot, collection, query, where, orderBy, limit, getDocs, collectionGroup } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';

// --- IMPORTS for Swiper ---
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { Container, Typography, Box, Paper, Button, CircularProgress, Grid, Card, CardContent, CardActions } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Groups';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

import HomePageEditModal from '../components/HomePageEditModal';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';

import { rrulestr } from 'rrule';
import { addMonths, isValid, format, addDays } from 'date-fns';

const editorConfig = {
    namespace: 'HomePageViewer',
    onError(error) { console.error(error); },
    nodes: [ HeadingNode, QuoteNode, ListItemNode, ListNode, CodeHighlightNode, CodeNode, AutoLinkNode, LinkNode ],
    editable: false,
};

const LexicalRenderer = ({ editorStateJSON }) => {
    let initialEditorState;
    try {
        if (typeof editorStateJSON === 'string' && editorStateJSON.trim().startsWith('{')) {
            initialEditorState = editorStateJSON;
        } else {
            const defaultState = {"root":{"children":[{"children":[{"text":editorStateJSON || ''}],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}};
            initialEditorState = JSON.stringify(defaultState);
        }
    } catch (e) {
        initialEditorState = '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
    }

    const editorConfigWithState = {
        ...editorConfig,
        editorState: initialEditorState,
    };

    return (
        <LexicalComposer initialConfig={editorConfigWithState}>
            <RichTextPlugin
                contentEditable={<ContentEditable style={{ outline: 'none' }} />}
                placeholder={null}
                ErrorBoundary={LexicalErrorBoundary}
            />
        </LexicalComposer>
    );
};


function HomePage() {
  const { userProfile, churchProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [homePageData, setHomePageData] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [latestSermon, setLatestSermon] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const canEditHomePage = userProfile?.canEditHomePage === true ||
                          userProfile?.role === 'Church Administrator' ||
                          userProfile?.role === 'Super Administrator';

  useEffect(() => {
    if (canEditHomePage && searchParams.get('action') === 'edit') {
      setIsEditModalOpen(true);
    }
  }, [searchParams, canEditHomePage]);

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    navigate('/', { replace: true });
  }

  useEffect(() => {
    if (!userProfile?.churchId) {
        setLoading(false);
        return;
    };
    const docRef = doc(db, 'churches', userProfile.churchId, 'siteContent', 'homePage');
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        setHomePageData(doc.data());
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userProfile?.churchId]);

  useEffect(() => {
    if (!userProfile?.churchId) return;

    const fetchEvents = async () => {
      const now = new Date();
      let allCombinedEvents = [];

      const mainEventsRef = collection(db, 'churches', userProfile.churchId, 'events');
      const mainEventsSnapshot = await getDocs(mainEventsRef);
      const fetchedMainEvents = mainEventsSnapshot.docs.flatMap(doc => {
        const data = doc.data();
        let originalStart = data.startAt?.toDate ? data.startAt.toDate() : null;
        let originalEnd = data.endAt?.toDate ? data.endAt.toDate() : originalStart;
        const isAllDay = data.isAllDay;
        const exdates = data.exdates || [];

        if (originalStart && !isValid(originalStart)) {
            originalStart = null;
        }
        if (originalEnd && !isValid(originalEnd)) {
            originalEnd = null;
        }
        if (!originalEnd && originalStart) {
            originalEnd = originalStart;
        }

        let eventTitle = data.title;
        if (data.isFeatured) {
            if (!eventTitle.startsWith('⭐ ')) {
                eventTitle = '⭐ ' + eventTitle;
            }
        } else {
            if (eventTitle.startsWith('⭐ ')) {
                eventTitle = eventTitle.substring(2);
            }
        }

        const baseEvent = {
            id: doc.id,
            title: eventTitle,
            start: originalStart,
            end: originalEnd,
            isFeatured: data.isFeatured || false,
            isGroupEvent: false,
            isVolunteerEvent: false,
            location: data.location,
            description: data.description,
            recurrenceRules: data.recurrenceRules,
            exdates: exdates,
            isAllDay: isAllDay,
        };

        if (data.recurrenceRules && data.recurrenceRules.length > 0 && originalStart) {
            try {
                const rruleString = data.recurrenceRules[0];
                const rrule = rrulestr(rruleString, { dtstart: originalStart });

                const futureDate = addMonths(now, 6);
                const occurrences = rrule.between(now, futureDate, true);

                return occurrences.map((occDate) => {
                    let newStart = occDate;
                    let newEnd = occDate;

                    if (!isAllDay) {
                        newStart = new Date(
                            occDate.getFullYear(),
                            occDate.getMonth(),
                            occDate.getDate(),
                            originalStart.getHours(),
                            originalStart.getMinutes(),
                            originalStart.getSeconds(),
                            originalStart.getMilliseconds()
                        );
                        newEnd = new Date(
                            occDate.getFullYear(),
                            occDate.getMonth(),
                            occDate.getDate(),
                            originalEnd.getHours(),
                            originalEnd.getMinutes(),
                            originalEnd.getSeconds(),
                            originalEnd.getMilliseconds()
                        );
                        if (originalEnd.getTime() > originalStart.getTime() && (originalEnd.getDate() !== originalStart.getDate() || originalEnd.getMonth() !== originalStart.getMonth() || originalEnd.getFullYear() !== originalStart.getFullYear())) {
                            newEnd = addDays(newEnd, 1);
                        }
                    } else {
                        newEnd = addDays(newEnd, 1);
                    }

                    const occurrenceFormatForExdate = format(newStart, "yyyyMMdd'T'HHmmss'Z'");
                    if (exdates.includes(occurrenceFormatForExdate)) {
                        return null;
                    }

                    return {
                        ...baseEvent,
                        start: newStart,
                        end: newEnd,
                    };
                }).filter(e => e !== null && isValid(e.start) && isValid(e.end));
            } catch (e) {
                console.error("Error expanding RRULE for homepage event:", e);
                return [];
            }
        } else if (originalStart && isValid(originalStart)) {
            return [{
                ...baseEvent,
                start: originalStart,
                end: originalEnd,
            }];
        }
        return [];
      });
      allCombinedEvents.push(...fetchedMainEvents.flat());


      // --- START OF FIX ---
      const groupEventsRef = collectionGroup(db, 'groupEvents');
      const groupEventsQuery = query(groupEventsRef, where('churchId', '==', userProfile.churchId));
      const groupEventsSnapshot = await getDocs(groupEventsQuery);
      
      const fetchedGroupEvents = groupEventsSnapshot.docs.flatMap(doc => {
        const data = doc.data();
        let originalStart = data.startAt?.toDate ? data.startAt.toDate() : null;
        let originalEnd = data.endAt?.toDate ? data.endAt.toDate() : originalStart;
        const isAllDay = data.isAllDay;
        const exdates = data.exdates || [];

        if (originalStart && !isValid(originalStart)) {
            originalStart = null;
        }
        if (originalEnd && !isValid(originalEnd)) {
            originalEnd = null;
        }
        if (!originalEnd && originalStart) {
            originalEnd = originalStart;
        }

        const baseEvent = {
            id: doc.id,
            title: `[${data.groupName || 'Group'}] ${data.title}`,
            start: originalStart,
            end: originalEnd,
            isFeatured: false, // Group events are not featured on home page
            isGroupEvent: true,
            isVolunteerEvent: false,
            location: data.location,
            description: data.description,
            recurrenceRules: data.recurrenceRules,
            exdates: exdates,
            isAllDay: isAllDay,
        };

        // Apply the same recurrence logic as main events
        if (data.recurrenceRules && data.recurrenceRules.length > 0 && originalStart) {
            try {
                const rruleString = data.recurrenceRules[0];
                const rrule = rrulestr(rruleString, { dtstart: originalStart });

                const futureDate = addMonths(now, 6);
                const occurrences = rrule.between(now, futureDate, true);

                return occurrences.map((occDate) => {
                    let newStart = occDate;
                    let newEnd = occDate;

                    if (!isAllDay) {
                        newStart = new Date(
                            occDate.getFullYear(),
                            occDate.getMonth(),
                            occDate.getDate(),
                            originalStart.getHours(),
                            originalStart.getMinutes(),
                            originalStart.getSeconds(),
                            originalStart.getMilliseconds()
                        );
                        newEnd = new Date(
                            occDate.getFullYear(),
                            occDate.getMonth(),
                            occDate.getDate(),
                            originalEnd.getHours(),
                            originalEnd.getMinutes(),
                            originalEnd.getSeconds(),
                            originalEnd.getMilliseconds()
                        );
                        if (originalEnd.getTime() > originalStart.getTime() && (originalEnd.getDate() !== originalStart.getDate() || originalEnd.getMonth() !== originalStart.getMonth() || originalEnd.getFullYear() !== originalStart.getFullYear())) {
                            newEnd = addDays(newEnd, 1);
                        }
                    } else {
                        newEnd = addDays(newEnd, 1);
                    }

                    const occurrenceFormatForExdate = format(newStart, "yyyyMMdd'T'HHmmss'Z'");
                    if (exdates.includes(occurrenceFormatForExdate)) {
                        return null;
                    }

                    return {
                        ...baseEvent,
                        start: newStart,
                        end: newEnd,
                    };
                }).filter(e => e !== null && isValid(e.start) && isValid(e.end));
            } catch (e) {
                console.error("Error expanding RRULE for group event:", e);
                return [];
            }
        } else if (originalStart && isValid(originalStart)) {
            return [{
                ...baseEvent,
                start: originalStart,
                end: originalEnd,
            }];
        }
        return [];
      });
      allCombinedEvents.push(...fetchedGroupEvents.flat());
      // --- END OF FIX ---


      const volunteerOpportunitiesRef = collection(db, 'churches', userProfile.churchId, 'volunteerOpportunities');
      const volunteerQuery = query(volunteerOpportunitiesRef, where('showOnMainCalendar', '==', true));
      const volunteerSnapshot = await getDocs(volunteerQuery);
      const fetchedVolunteerEvents = volunteerSnapshot.docs.map(doc => {
        const data = doc.data();
        const start = data.date?.toDate ? data.date.toDate() : null;
        const end = data.date?.toDate ? data.date.toDate() : null;
        return {
          id: doc.id,
          title: `[Volunteer] ${data.title}`,
          start: start,
          end: end,
          isFeatured: false,
          isGroupEvent: false,
          isVolunteerEvent: true,
          location: data.location,
          description: data.description,
        };
      }).filter(e => e.start instanceof Date);
      allCombinedEvents.push(...fetchedVolunteerEvents);

      const upcomingSortedEvents = allCombinedEvents
        .filter(event => {
          const comparisonDate = event.end || event.start;
          return comparisonDate && comparisonDate instanceof Date && comparisonDate >= now;
        })
        .sort((a, b) => {
          if (a.isFeatured && !b.isFeatured) return -1;
          if (!a.isFeatured && b.isFeatured) return 1;
          return a.start.getTime() - b.start.getTime();
        })
        .slice(0, 3);

      setUpcomingEvents(upcomingSortedEvents);
    };

    const fetchSermon = async () => {
      const sermonsRef = collection(db, 'churches', userProfile.churchId, 'sermons');
      const q = query(sermonsRef, orderBy('date', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        setLatestSermon({ id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() });
      }
    };

    fetchEvents();
    fetchSermon();
  }, [userProfile?.churchId]);

  if (loading) {
    return <CircularProgress />;
  }

  const isLexicalContentEmpty = (editorStateJSON) => {
    if (!editorStateJSON) return true;
    try {
      const editorState = JSON.parse(editorStateJSON);
      if (editorState.root && editorState.root.children) {
        if (editorState.root.children.length === 1) {
          const firstChild = editorState.root.children[0];
          if (firstChild.type === 'paragraph' && (!firstChild.children || firstChild.children.length === 0)) {
            return true;
          }
          if (firstChild.type === 'paragraph' && firstChild.children.length === 1 && firstChild.children[0].type === 'text' && firstChild.children[0].text.trim() === '') {
            return true;
          }
        }
        let hasContent = false;
        editorState.root.children.forEach(node => {
          if (node.children) {
            node.children.forEach(child => {
              if (child.type === 'text' && child.text.trim() !== '') {
                hasContent = true;
              }
            });
          }
        });
        return !hasContent;
      }
    } catch (e) {
      console.warn("Error parsing editorStateJSON for emptiness check:", e);
      return true;
    }
    return false;
  };

  return (
    <Container maxWidth="lg">

      <Grid container spacing={1} sx={{ mb: 4 }} justifyContent="center">
        <Grid item xs={6} sm={3}><Button fullWidth variant="contained" startIcon={<EventIcon />} onClick={() => navigate('/calendar')}>Events</Button></Grid>
        <Grid item xs={6} sm={3}><Button fullWidth variant="contained" startIcon={<GroupIcon />} onClick={() => navigate('/groups')}>Groups</Button></Grid>
        <Grid item xs={6} sm={3}><Button fullWidth variant="contained" startIcon={<VolunteerActivismIcon />} onClick={() => navigate('/volunteer')}>Volunteer</Button></Grid>
        <Grid item xs={6} sm={3}><Button fullWidth variant="contained" startIcon={<PlaylistAddCheckIcon />} onClick={() => navigate('/prayer-wall')}>Prayer Wall</Button></Grid>
      </Grid>

      {homePageData?.heroImages && homePageData.heroImages.length > 0 && (
        <Paper elevation={3} sx={{ mb: 4, width: '100%', maxWidth: { xs: '100%', sm: '800px', md: '1000px' }, margin: '0 auto', overflow: 'hidden' }}>
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', backgroundColor: churchProfile?.theme?.primaryColor || 'black' }}>
                <Swiper
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    spaceBetween={30}
                    centeredSlides={true}
                    autoplay={{ delay: 4000, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    navigation={true}
                    modules={[Autoplay, Pagination, Navigation]}
                >
                    {homePageData.heroImages.map((image, index) => (
                    <SwiperSlide key={index} style={{ backgroundColor: churchProfile?.theme?.primaryColor || 'black' }}>
                        <img src={image.url} alt={`Slide ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </SwiperSlide>
                    ))}
                </Swiper>
            </Box>
        </Paper>
      )}

      {homePageData?.welcomeMessage && !isLexicalContentEmpty(homePageData.welcomeMessage) && (
        <Paper elevation={1} sx={{ p: {xs: 2, md: 4}, mb: 4, overflowX: 'auto' }}>
          <Typography variant="h5" gutterBottom>Welcome Message</Typography>
          <LexicalRenderer editorStateJSON={homePageData.welcomeMessage} />
        </Paper>
      )}

      {homePageData?.verseOfTheWeek && !isLexicalContentEmpty(homePageData.verseOfTheWeek) && (
        <Paper elevation={1} sx={{ p: {xs: 2, md: 4}, mb: 4, overflowX: 'auto' }}>
          <Typography variant="h5" gutterBottom>Verse of the Week</Typography>
          <LexicalRenderer editorStateJSON={homePageData.verseOfTheWeek} />
        </Paper>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {homePageData?.pastorMessage && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>A Word from the Pastor</Typography>
                <Typography variant="body1" sx={{whiteSpace: 'pre-wrap'}}>{homePageData.pastorMessage}</Typography>
              </CardContent>
            </Card>
          )}

          {latestSermon && (
             <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>Latest Sermon</Typography>
                <Typography variant="h6">{latestSermon.title}</Typography>
                <Typography variant="body2" color="text.secondary">Speaker: {latestSermon.speaker}</Typography>
              </CardContent>
              <CardActions>
                 <Button size="small" href={latestSermon.videoLink} target="_blank" rel="noopener noreferrer">Watch Now</Button>
              </CardActions>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Upcoming Events</Typography>
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map(event => (
                <Box key={event.id + event.start.toISOString()} sx={{ mb: 2 }}>
                  <Typography fontWeight="bold">{event.title}</Typography>
                  <Typography variant="body2">{event.start?.toLocaleString()}</Typography>
                </Box>
              ))
            ) : (
              <Typography>No upcoming events.</Typography>
            )}
            <Button size="small" onClick={() => navigate('/calendar')}>View Full Calendar</Button>
          </Paper>
        </Grid>
      </Grid>

      {canEditHomePage && (
        <HomePageEditModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          homePageData={homePageData}
        />
      )}
    </Container>
  );
}


export default HomePage;